import { connect } from 'react-redux';
import { AnyAction } from 'redux';
import { ProductPriceHistoryErrorResponse } from '~/Api/Product/PriceHistory';
import config from '~/config';
import { handleError } from '~/ErrorHandler';
import { resolveIsLicensed } from '~/FormattedProductPrice';
import { fetchProduct, Product } from '~/Product';
import { fetchPriceHistory, ProductPriceHistoryRecord, selectPriceHistory } from '~/ProductPriceHistory';
import { ApplicationState, ThunkDispatch } from '~/Store';

import LoadablePriceChart from './LoadablePriceChart';
import { ChartDataset } from 'chart.js';

type Point = { x: Date; y: number | null };

export type ChartHistory = ChartDataset<'line', (number | Point | null)[]> & { steppedLine: boolean };

function mapChartData(prices: ProductPriceHistoryRecord[]) {
	const byPriceType: { [key: string]: ChartHistory } = {};
	const records: ChartHistory[] = [];
	const points: Date[] = [];
	const now = new Date();

	config.product.chartPrices.forEach((priceType) => {
		const history: ChartHistory = {
			label: priceType.label,
			steppedLine: true,
			borderColor: priceType.color,
			backgroundColor: priceType.color,
			data: [],
		};
		byPriceType[priceType.code] = history;
		records.push(history);
	});

	[...prices].reverse().forEach((price) => {
		const endDate = price.dateInterval.end !== null ? price.dateInterval.end : now;

		byPriceType[price.productPrice.type].data.push({
			x: price.dateInterval.start,
			y: price.productPrice.price?.roundedValue() || 0,
		});

		byPriceType[price.productPrice.type].data.push({
			x: endDate,
			y: price.productPrice.price?.roundedValue() || 0,
		});

		if (price.dateInterval.end !== null) {
			byPriceType[price.productPrice.type].data.push({
				x: endDate,
				y: null,
			});
		}

		points.push(price.dateInterval.start);
		points.push(endDate);
	});

	points.sort((a, b) => (a.getTime() > b.getTime() ? 1 : 0));

	const firstPrice = prices[0];
	return {
		records,
		points,
		currency: firstPrice ? firstPrice.productPrice.price?.currency || null : null,
	};
}

function mapStateToProps(
	state: ApplicationState,
	props: {
		product: Product;
	}
): {
	isFetching: boolean;
	isLicenced: boolean;
	records: ChartHistory[];
	points: Date[];
	currency: string | null;
} {
	const history = selectPriceHistory(state.productPriceHistory, props.product.id, null);
	const isLicenced = Boolean(
		resolveIsLicensed(
			state.accessToken.accessToken,
			state.customerDatasourceLicences,
			props.product.dataset.source.type
		)
	);

	return {
		isFetching: history?.isFetching || isLicenced === null,
		isLicenced,
		...mapChartData(history?.prices || []),
	};
}

function mapDispatchToProps(dispatch: ThunkDispatch<AnyAction>): Record<never, never> {
	return {
		onFetch: (product: Product) => {
			return dispatch(fetchPriceHistory(product.id, null)).catch((response) => {
				if (!(response instanceof ProductPriceHistoryErrorResponse)) {
					dispatch(handleError(response));
					return;
				}
				if (response.handleCustomerWithoutLicence()) {
					dispatch(fetchProduct(product.id)).catch((response) => dispatch(handleError(response)));
				}
				dispatch(handleError(response));
			});
		},
	};
}

export default connect(mapStateToProps, mapDispatchToProps)(LoadablePriceChart);
